import { useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { api, formatApiError } from "@/lib/api";
import { EMPTY_REVIEW, photoUrl, reviewPayload } from "@/lib/reviews";
import { ReviewFields } from "@/components/site/ReviewFields";

const toForm = (r) => r ? { ...EMPTY_REVIEW, ...r, photos: r.photo_ids.map((id) => ({ id, preview: photoUrl(id) })) }
  : { ...EMPTY_REVIEW, status: "published", featured: false };

export const ReviewDialog = ({ open, review, onClose, onSaved }) => {
  const [form, setForm] = useState(toForm(review));
  const [busy, setBusy] = useState(false);
  const [saving, setSaving] = useState(false);
  useEffect(() => { if (open) setForm(toForm(review)); }, [open, review]);
  const tid = (s) => `review-dialog-${s}`;

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const { id, source, created_at, updated_at, published_at, photo_ids, ip, ...rest } = form;
      const payload = reviewPayload(rest);
      const { data } = review ? await api.put(`/admin/reviews/${review.id}`, payload) : await api.post("/admin/reviews", payload);
      toast.success(review ? "Review updated" : "Review added");
      onSaved(data);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto bg-white" data-testid="review-dialog">
        <DialogHeader><DialogTitle>{review ? "Edit review" : "Add guest review"}</DialogTitle></DialogHeader>
        <form onSubmit={save} className="grid gap-4 sm:grid-cols-2">
          <ReviewFields form={form} setForm={setForm} tid={tid} onBusy={setBusy} />
          <div className="space-y-1.5">
            <Label htmlFor={tid("phone")}>Guest phone (private)</Label>
            <Input id={tid("phone")} maxLength={20} value={form.phone} onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))} className="h-11" data-testid={tid("phone-input")} />
          </div>
          <div className="space-y-1.5">
            <Label>Status</Label>
            <Select value={form.status} onValueChange={(v) => setForm((f) => ({ ...f, status: v }))}>
              <SelectTrigger className="h-11" data-testid={tid("status-select")}><SelectValue /></SelectTrigger>
              <SelectContent className="bg-white">
                <SelectItem value="published">Published (on homepage)</SelectItem>
                <SelectItem value="pending">Pending approval</SelectItem>
                <SelectItem value="hidden">Hidden</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <label className="sm:col-span-2 flex items-center gap-3 text-sm text-slate-700">
            <Switch checked={form.featured} onCheckedChange={(v) => setForm((f) => ({ ...f, featured: v }))} data-testid={tid("featured-switch")} />
            Feature this review first on the homepage
          </label>
          <div className="sm:col-span-2 flex justify-end gap-2">
            <Button type="button" variant="outline" onClick={onClose} data-testid={tid("cancel")}>Cancel</Button>
            <Button type="submit" disabled={saving || busy} className="bg-ocean hover:bg-navy" data-testid={tid("save")}>
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : busy ? "Uploading..." : "Save review"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};
